import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { usePermissions } from './auth/usePermissions.js';
import { ORG } from './auth/capabilities.js';
import { DeniedState } from './auth/DeniedState.jsx';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill } from '../shared/ui';
import { PageHead, StatCard, TabBar, Th } from '../platform/_ui';
import * as licenseApi from '../api/license.api.js';
import {
  useOrgRuntime,
  useOrganization,
  useSubscription,
  useLimits,
  useUsage,
  useOrgFeatures,
} from './OrganizationRuntimeContext.jsx';

/* ─── Constants ──────────────────────────────────────────────────────────── */

const LIC_META = {
  USER:      { icon: 'Users',        color: 'var(--nx-accent, #125AB0)', label: 'User Licenses',      unit: 'seats'    },
  WORKSPACE: { icon: 'LayoutGrid',   color: 'var(--nx-accent)', label: 'Workspace Licenses', unit: 'workspaces'},
  AGENT:     { icon: 'Cpu',          color: 'var(--nx-accent)', label: 'Agent Licenses',     unit: 'agents'   },
  BOT:       { icon: 'Bot',          color: 'var(--nx-accent)', label: 'Bot Licenses',       unit: 'bots'     },
  STORAGE:   { icon: 'HardDrive',    color: 'var(--nx-accent)', label: 'Storage Licenses',   unit: 'GB'       },
  API:       { icon: 'Zap',          color: 'var(--nx-accent)', label: 'API Licenses',       unit: 'calls/day'},
  MESSAGE:   { icon: 'MessageSquare',color: 'var(--nx-accent)', label: 'Message Licenses',   unit: 'msgs/mo'  },
};

const LIC_ORDER = ['USER', 'WORKSPACE', 'AGENT', 'BOT', 'STORAGE', 'API', 'MESSAGE'];

const RESOURCE_ROWS = [
  { key: 'users',           limitKey: 'maxUsers',           label: 'Users',          icon: 'Users',        color: 'var(--nx-accent, #125AB0)', unit: 'users'    },
  { key: 'workspaces',      limitKey: 'maxWorkspaces',      label: 'Workspaces',     icon: 'LayoutGrid',   color: 'var(--nx-accent)', unit: 'workspaces'},
  { key: 'agents',          limitKey: 'maxAgents',          label: 'AI Agents',      icon: 'Cpu',          color: 'var(--nx-accent)', unit: 'agents'   },
  { key: 'bots',            limitKey: 'maxBots',            label: 'Bots',           icon: 'Bot',          color: 'var(--nx-accent)', unit: 'bots'     },
  { key: 'storageGb',       limitKey: 'maxStorageGb',       label: 'Storage',        icon: 'HardDrive',    color: 'var(--nx-accent)', unit: 'GB'       },
  { key: 'apiCallsToday',   limitKey: 'maxApiCallsPerDay',  label: 'API Calls/Day',  icon: 'Zap',          color: 'var(--nx-accent)', unit: 'calls'    },
  { key: 'messagesThisMonth',limitKey:'maxMessagesPerMonth',label: 'Messages/Month', icon: 'MessageSquare',color: 'var(--nx-accent)', unit: 'msgs'     },
];

const SUB_STATUS_TONE  = { ACTIVE: 'green', TRIAL: 'blue', SUSPENDED: 'amber', CANCELLED: 'gray', EXPIRED: 'red'  };
const SUB_STATUS_LABEL = { ACTIVE: 'Active', TRIAL: 'Trial', SUSPENDED: 'Suspended', CANCELLED: 'Cancelled', EXPIRED: 'Expired' };
const TIER_TONE        = { STARTER: 'blue', PROFESSIONAL: 'purple', ENTERPRISE: 'amber' };
const BILLING_LABEL    = { MONTHLY: 'Monthly', ANNUAL: 'Annual', TRIAL: 'Trial' };
const LIC_STATUS_TONE  = { ACTIVE: 'green', EXPIRED: 'red' };
const INSIGHT_ICON_COLOR = { red: 'var(--nx-danger)', amber: 'var(--nx-warning)', gray: 'var(--nx-text-muted)' };

const CAT_META = {
  crm:       { label: 'CRM',             icon: 'Users2',        color: 'var(--nx-accent, #125AB0)' },
  analytics: { label: 'Analytics',       icon: 'BarChart2',     color: 'var(--nx-accent)' },
  channels:  { label: 'Channels',        icon: 'MessageCircle', color: 'var(--nx-accent)' },
  storage:   { label: 'Storage',         icon: 'HardDrive',     color: 'var(--nx-accent)' },
  ai:        { label: 'AI & Automation', icon: 'Cpu',           color: 'var(--nx-accent)' },
  api:       { label: 'Developer API',   icon: 'Code2',         color: 'var(--nx-accent)' },
  org:       { label: 'Organization',    icon: 'Building2',     color: 'var(--nx-accent)' },
};

const TABS = [
  { id: 'overview',    label: 'Overview',    icon: 'LayoutDashboard' },
  { id: 'allocations', label: 'Allocations', icon: 'Key'             },
  { id: 'features',    label: 'Features',    icon: 'Sparkles'        },
  { id: 'insights',    label: 'Insights',    icon: 'TrendingUp'      },
  { id: 'history',     label: 'History',     icon: 'Clock'           },
];

/* ─── Helpers ────────────────────────────────────────────────────────────── */

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const fmtFeature = (f) => {
  const part = f.includes('.') ? f.split('.').slice(1).join('.') : f;
  return part.replaceAll('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const daysUntil = (iso) => {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
};

const licRenewal = (renews) => {
  if (!renews) return { label: 'No expiry', tone: 'gray' };
  const d = Math.ceil((new Date(renews).getTime() - Date.now()) / 86400000);
  if (d < 0)   return { label: 'Expired',        tone: 'red'   };
  if (d <= 30) return { label: `${d}d left`,      tone: 'amber' };
  return { label: fmt(renews), tone: 'green' };
};

function computeLicenseInsights(lic) {
  const { type, allotted, used, status, renews, utilizationPct } = lic;
  const meta = LIC_META[type] ?? { label: type, unit: 'units' };
  const ins = [];
  if (status === 'EXPIRED') {
    ins.push({ sev: 'red', icon: 'AlertCircle', title: `${meta.label} expired`, desc: 'Renew through your platform administrator.' });
    return ins;
  }
  if (allotted > 0) {
    const pct = utilizationPct ?? Math.round(used / allotted * 100);
    if (pct >= 100) {
      ins.push({ sev: 'red', icon: 'AlertCircle', title: `${meta.label} at capacity`, desc: `${used} of ${allotted} ${meta.unit} in use. Contact your admin to increase allocation.` });
    } else if (pct >= 80) {
      ins.push({ sev: 'amber', icon: 'AlertTriangle', title: `${meta.label} nearing capacity`, desc: `${pct}% utilized (${used}/${allotted} ${meta.unit}).` });
    } else if (used === 0) {
      ins.push({ sev: 'gray', icon: 'Info', title: `${meta.label} unused`, desc: `0 of ${allotted} ${meta.unit} allocated.` });
    }
  }
  const d = daysUntil(renews);
  if (d !== null && d >= 0 && d <= 30) {
    const plural = d === 1 ? '' : 's';
    ins.push({ sev: 'amber', icon: 'Calendar', title: `${meta.label} renewing soon`, desc: `Renews in ${d} day${plural} (${fmt(renews)}).` });
  }
  return ins;
}

function computeInsights(licenses, limits, usage) {
  const ins = licenses.flatMap(computeLicenseInsights);
  if (limits.maxWorkspaces > 0) {
    const wsUsed = usage.workspaces ?? 0;
    if (wsUsed >= limits.maxWorkspaces) {
      ins.push({ sev: 'red', icon: 'AlertCircle', title: 'Workspace limit reached', desc: `${wsUsed} of ${limits.maxWorkspaces} workspaces in use.` });
    } else if (wsUsed / limits.maxWorkspaces >= 0.8) {
      ins.push({ sev: 'amber', icon: 'AlertTriangle', title: 'Workspace limit nearing', desc: `${wsUsed} of ${limits.maxWorkspaces} workspaces in use.` });
    }
  }
  return ins;
}

function buildTimeline(org, subscription) {
  const events = [];
  if (org?.createdAt) {
    events.push({ icon: 'Building2', label: 'Organization created', date: org.createdAt });
  }
  if (subscription?.isInTrial && subscription.trialEndsAt) {
    events.push({ icon: 'Clock', label: 'Trial period ends', date: subscription.trialEndsAt });
  }
  if (subscription?.currentPeriodEnd) {
    events.push({ icon: 'Calendar', label: 'Current billing period ends', date: subscription.currentPeriodEnd });
  }
  if (subscription?.renewalAt) {
    events.push({ icon: 'RefreshCw', label: 'Next renewal', date: subscription.renewalAt });
  }
  return events.sort((a, b) => new Date(a.date) - new Date(b.date));
}

/* ─── Shared UI ──────────────────────────────────────────────────────────── */

function getBarColor(pct, defaultColor) {
  if (pct >= 100) return 'var(--nx-danger)';
  if (pct >= 80)  return 'var(--nx-warning)';
  return defaultColor;
}

function ProgressBar({ used, max, color, unit }) {
  if (!max || max === 0) {
    return <span style={{ fontSize: 12, color: 'var(--nx-text-muted)', fontStyle: 'italic' }}>Unlimited</span>;
  }
  const pct = Math.min(100, Math.round(used / max * 100));
  const barColor = getBarColor(pct, color);
  const remLabel = pct >= 100 ? 'Limit reached' : `${max - used} remaining · ${pct}%`;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--nx-text-muted)', marginBottom: 3 }}>
        <span>{used} {unit} used</span>
        <span>{remLabel}</span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: 'var(--nx-border)' }}>
        <div style={{ height: 6, borderRadius: 3, width: pct + '%', background: barColor, transition: 'width 0.4s' }} />
      </div>
    </div>
  );
}

function LoadingSkeleton({ rows = 3 }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={'sk-' + i} className="nx-skeleton" style={{ height: 88, borderRadius: 8 }} />
      ))}
    </div>
  );
}

/* ─── Overview Tab ───────────────────────────────────────────────────────── */

const SUB_FIELDS = [
  { key: 'billingCycle',     label: 'Billing Cycle', icon: 'CreditCard', fmt: (v) => BILLING_LABEL[v] ?? v ?? '—' },
  { key: 'renewalAt',        label: 'Renewal In',    icon: 'RefreshCw',  fmt: (v, sub) => sub?.daysUntilRenewal == null ? fmt(v) : `${sub.daysUntilRenewal}d` },
  { key: 'currentPeriodEnd', label: 'Period End',    icon: 'Calendar',   fmt: (v) => fmt(v) },
  { key: 'trialEndsAt',      label: 'Trial Ends',    icon: 'Clock',      fmt: (v, sub) => sub?.isInTrial ? fmt(v) : '—' },
];

function SubscriptionCard({ org, subscription }) {
  if (!subscription) {
    return (
      <div className="nx-cc-chart-card" style={{ marginBottom: 16 }}>
        <div className="nx-empty"><NexusIcon name="Loader" size={16} /><span>Loading subscription…</span></div>
      </div>
    );
  }
  const statusTone  = SUB_STATUS_TONE[subscription.status]  ?? 'gray';
  const statusLabel = SUB_STATUS_LABEL[subscription.status] ?? subscription.status ?? '—';
  const tierTone    = TIER_TONE[subscription.planTier] ?? 'gray';
  return (
    <div className="nx-cc-chart-card" style={{ marginBottom: 16 }}>
      <div className="nx-cc-chart-head">
        <div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{subscription.planName ?? '—'}</div>
          <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>{org?.name}</div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <Pill tone={tierTone}>{subscription.planTier}</Pill>
          <Pill tone={statusTone}>{statusLabel}</Pill>
          {subscription.isInTrial && <Pill tone="blue">Trial</Pill>}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 10, marginTop: 14 }}>
        {SUB_FIELDS.map(({ key, label, icon, fmt: fmtVal }) => (
          <div key={key} style={{ background: 'var(--nx-surface-alt)', borderRadius: 8, padding: '10px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <NexusIcon name={icon} size={12} style={{ color: 'var(--nx-text-muted)' }} />
              <span style={{ fontSize: 11, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
            </div>
            <span style={{ fontSize: 14, fontWeight: 600 }}>{fmtVal(subscription[key], subscription)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function OverviewTab({ org, subscription, limits, usage, licenses }) {
  const userLic      = licenses.find((l) => l.type === 'USER');
  const totalSeats   = userLic?.allotted ?? 0;
  const usedSeats    = userLic?.used ?? 0;
  const availSeats   = totalSeats > 0 ? Math.max(0, totalSeats - usedSeats) : null;
  const planTone     = TIER_TONE[subscription?.planTier] ? 'purple' : 'blue';
  return (
    <>
      <SubscriptionCard org={org} subscription={subscription} />
      <div className="nx-cc-stat-grid nx-kpi-5">
        <StatCard label="Plan"        value={subscription?.planName ?? '—'}     icon="Tag"         color={planTone}  />
        <StatCard label="Total Seats" value={totalSeats > 0 ? totalSeats : '∞'} icon="Key"         color="blue"      />
        <StatCard label="Seats Used"  value={usedSeats}                          icon="Users"       color="blue"      />
        <StatCard label="Available"   value={availSeats ?? '∞'}                  icon="CheckCircle" color="blue"      />
        <StatCard label="Workspaces"  value={usage.workspaces ?? 0}              icon="LayoutGrid"  color="purple"    />
      </div>
      <div className="nx-cc-chart-card">
        <div className="nx-cc-chart-head">
          <span className="nx-cc-chart-title">Resource Limits</span>
          <span className="nx-muted nx-mini">0 = unlimited</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 14 }}>
          {RESOURCE_ROWS.map((r) => {
            const used = usage[r.key] ?? 0;
            const max  = limits[r.limitKey] ?? 0;
            return (
              <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ '--_lc': r.color, width: 32, height: 32, borderRadius: 8, background: 'color-mix(in srgb, var(--_lc) 9%, transparent)', color: 'var(--_lc)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <NexusIcon name={r.icon} size={14} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{r.label}</div>
                  <ProgressBar used={used} max={max} color={r.color} unit={r.unit} />
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, flexShrink: 0, minWidth: 56, textAlign: 'right' }}>
                  {max === 0 ? `${used} / ∞` : `${used} / ${max}`}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

/* ─── Allocations Tab ────────────────────────────────────────────────────── */

function LicenseCard({ lic }) {
  const meta        = LIC_META[lic.type] ?? { icon: 'Key', color: '#888', label: lic.type, unit: 'units' };
  const statusTone  = LIC_STATUS_TONE[lic.status] ?? 'amber';
  const renewal     = licRenewal(lic.renews);
  const isUnlimited = !lic.allotted || lic.allotted === 0;
  const avail       = isUnlimited ? null : Math.max(0, lic.allotted - (lic.used ?? 0));
  return (
    <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 10, padding: 16, border: '1px solid var(--nx-border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ '--_lc': meta.color, width: 36, height: 36, borderRadius: 8, background: 'color-mix(in srgb, var(--_lc) 9%, transparent)', color: 'var(--_lc)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <NexusIcon name={meta.icon} size={16} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>{meta.label}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            <Pill tone={statusTone}>{lic.status}</Pill>
            <Pill tone={renewal.tone}>{renewal.label}</Pill>
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 800 }}>{isUnlimited ? '∞' : lic.allotted}</div>
          <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{meta.unit}</div>
        </div>
      </div>
      {isUnlimited ? (
        <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', fontStyle: 'italic' }}>Unlimited allocation</div>
      ) : (
        <>
          <ProgressBar used={lic.used ?? 0} max={lic.allotted} color={meta.color} unit={meta.unit} />
          <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12, color: 'var(--nx-text-muted)' }}>
            <span><b style={{ color: 'var(--nx-text)' }}>{lic.used ?? 0}</b> in use</span>
            <span><b style={{ color: 'var(--nx-text)' }}>{avail}</b> available</span>
            {lic.utilizationPct != null && <span><b style={{ color: 'var(--nx-text)' }}>{lic.utilizationPct}%</b> utilized</span>}
          </div>
        </>
      )}
    </div>
  );
}

function AllocationTab({ licenses, loading, error }) {
  if (loading) return <LoadingSkeleton rows={4} />;
  if (error) return (
    <div className="nx-empty" style={{ flexDirection: 'column', gap: 6 }}>
      <NexusIcon name="AlertCircle" size={22} style={{ color: 'var(--nx-danger)' }} />
      <span style={{ fontWeight: 600 }}>Failed to load licenses</span>
      <span className="nx-muted nx-mini">{error}</span>
    </div>
  );
  if (licenses.length === 0) return (
    <div className="nx-empty" style={{ flexDirection: 'column', gap: 6 }}>
      <NexusIcon name="Key" size={22} />
      <span style={{ fontWeight: 600 }}>No licenses assigned</span>
      <span className="nx-muted nx-mini">Contact your platform administrator to assign licenses to this organization.</span>
    </div>
  );
  const sorted = LIC_ORDER.map((t) => licenses.find((l) => l.type === t)).filter(Boolean);
  const rest   = licenses.filter((l) => !LIC_ORDER.includes(l.type));
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 12 }}>
      {[...sorted, ...rest].map((l) => <LicenseCard key={l.id} lic={l} />)}
    </div>
  );
}

/* ─── Features Tab ───────────────────────────────────────────────────────── */

function FeatureCategoryCard({ cat, catFeatures }) {
  const meta = CAT_META[cat] ?? { label: cat, icon: 'Circle', color: '#888' };
  return (
    <div style={{ background: 'var(--nx-surface-alt)', borderRadius: 10, padding: 16, border: '1px solid var(--nx-border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ '--_lc': meta.color, width: 32, height: 32, borderRadius: 8, background: 'color-mix(in srgb, var(--_lc) 9%, transparent)', color: 'var(--_lc)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <NexusIcon name={meta.icon} size={14} />
        </div>
        <span style={{ fontSize: 13, fontWeight: 700 }}>{meta.label}</span>
        <span className="nx-muted nx-mini" style={{ marginLeft: 'auto' }}>{catFeatures.length} feature{catFeatures.length === 1 ? '' : 's'}</span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {catFeatures.map((f) => (
          <div key={f} style={{ '--_lc': meta.color, display: 'flex', alignItems: 'center', gap: 4, background: 'color-mix(in srgb, var(--_lc) 8%, transparent)', color: 'var(--_lc)', borderRadius: 4, padding: '3px 8px', fontSize: 11, fontWeight: 600 }}>
            <NexusIcon name="Check" size={10} />
            {fmtFeature(f)}
          </div>
        ))}
      </div>
    </div>
  );
}

function FeaturesTab({ features }) {
  if (features.length === 0) return (
    <div className="nx-empty" style={{ flexDirection: 'column', gap: 6 }}>
      <NexusIcon name="Sparkles" size={22} />
      <span style={{ fontWeight: 600 }}>No features assigned</span>
      <span className="nx-muted nx-mini">Feature entitlements come from your active subscription plan.</span>
    </div>
  );
  const grouped = features.reduce((acc, f) => {
    const cat = f.includes('.') ? f.split('.')[0] : 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(f);
    return acc;
  }, {});
  const known  = Object.keys(CAT_META).filter((k) => grouped[k]);
  const others = Object.keys(grouped).filter((k) => !Object.keys(CAT_META).includes(k));
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <NexusIcon name="Sparkles" size={14} style={{ color: 'var(--nx-accent)' }} />
        <span style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>{features.length} features enabled by your plan</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 12 }}>
        {[...known, ...others].map((cat) => (
          <FeatureCategoryCard key={cat} cat={cat} catFeatures={grouped[cat]} />
        ))}
      </div>
    </>
  );
}

/* ─── Insights Tab ───────────────────────────────────────────────────────── */

function InsightRow({ ins }) {
  const iconColor = INSIGHT_ICON_COLOR[ins.sev] ?? 'var(--nx-text-muted)';
  return (
    <div style={{ display: 'flex', gap: 12, padding: '12px 14px', borderRadius: 8, background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)' }}>
      <div style={{ flexShrink: 0, marginTop: 1 }}>
        <NexusIcon name={ins.icon} size={16} style={{ color: iconColor }} />
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{ins.title}</div>
        <div style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>{ins.desc}</div>
      </div>
    </div>
  );
}

function InsightsTab({ licenses, limits, usage }) {
  const insights = computeInsights(licenses, limits, usage);
  if (insights.length === 0) return (
    <div className="nx-empty" style={{ flexDirection: 'column', gap: 8 }}>
      <NexusIcon name="CheckCircle" size={28} style={{ color: 'var(--nx-success,#16a34a)' }} />
      <span style={{ fontWeight: 600 }}>All clear</span>
      <span className="nx-muted nx-mini">No capacity warnings or license issues detected.</span>
    </div>
  );
  const redIns   = insights.filter((i) => i.sev === 'red');
  const amberIns = insights.filter((i) => i.sev === 'amber');
  const otherIns = insights.filter((i) => i.sev !== 'red' && i.sev !== 'amber');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {redIns.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-danger)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Critical</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {redIns.map((ins) => <InsightRow key={ins.title} ins={ins} />)}
          </div>
        </div>
      )}
      {amberIns.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-warning)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Warnings</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {amberIns.map((ins) => <InsightRow key={ins.title} ins={ins} />)}
          </div>
        </div>
      )}
      {otherIns.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Informational</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {otherIns.map((ins) => <InsightRow key={ins.title} ins={ins} />)}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── History Tab ────────────────────────────────────────────────────────── */

function TimelineEvent({ ev, isLast }) {
  return (
    <div style={{ display: 'flex', gap: 12, paddingBottom: isLast ? 0 : 20, position: 'relative' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--nx-accent-tint)', color: 'var(--nx-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
          <NexusIcon name={ev.icon} size={12} />
        </div>
        {!isLast && <div style={{ width: 2, flex: 1, background: 'var(--nx-border)', marginTop: 4 }} />}
      </div>
      <div style={{ paddingTop: 4 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{ev.label}</div>
        <div style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>{fmt(ev.date)}</div>
      </div>
    </div>
  );
}

function HistoryTab({ org, subscription, history, historyLoading }) {
  const timeline = buildTimeline(org, subscription);
  return (
    <>
      <div className="nx-cc-chart-card" style={{ marginBottom: 16 }}>
        <div className="nx-cc-chart-head">
          <span className="nx-cc-chart-title">Subscription Timeline</span>
        </div>
        {timeline.length === 0 ? (
          <div className="nx-empty" style={{ padding: '16px 0' }}>
            <span className="nx-muted nx-mini">No timeline data available.</span>
          </div>
        ) : (
          <div style={{ marginTop: 16 }}>
            {timeline.map((ev, i) => <TimelineEvent key={ev.date + ev.label} ev={ev} isLast={i === timeline.length - 1} />)}
          </div>
        )}
      </div>
      <div className="nx-cc-chart-card">
        <div className="nx-cc-chart-head">
          <span className="nx-cc-chart-title">License Change History</span>
        </div>
        {historyLoading && (
          <div className="nx-empty" style={{ padding: '16px 0' }}>
            <NexusIcon name="Loader" size={16} /><span>Loading…</span>
          </div>
        )}
        {!historyLoading && history.length === 0 && (
          <div className="nx-empty" style={{ flexDirection: 'column', gap: 6, padding: '24px 0' }}>
            <NexusIcon name="Clock" size={22} />
            <span style={{ fontWeight: 600 }}>No license history</span>
            <span className="nx-muted nx-mini">License change events will appear here when plan changes occur.</span>
          </div>
        )}
        {!historyLoading && history.length > 0 && (
          <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
            <thead><tr>
              <Th icon="Calendar">Date</Th>
              <Th icon="Tag">Event</Th>
              <Th icon="Users">Change</Th>
              <Th icon="User">By</Th>
            </tr></thead>
            <tbody>{history.map((h, i) => (
              <tr key={h.id ?? i} className="nx-tr">
                <td><span className="nx-muted nx-mini">{fmt(h.createdAt)}</span></td>
                <td><Pill tone="gray">{h.action ?? h.event ?? '—'}</Pill></td>
                <td><span className="nx-muted">{h.seats ?? h.change ?? '—'}</span></td>
                <td><span className="nx-muted">{h.changedBy ?? h.actorEmail ?? h.actor ?? '—'}</span></td>
              </tr>
            ))}</tbody>
          </table></div></div>
        )}
      </div>
    </>
  );
}

/* ─── Main ───────────────────────────────────────────────────────────────── */

export default function OrgLicenses() {
  const { orgId }  = useParams();
  const navigate   = useNavigate();
  const [searchParams] = useSearchParams();
  const perm       = usePermissions();

  const returnTo    = searchParams.get('returnTo');
  const returnLabel = searchParams.get('returnLabel') || 'Back';

  const { loading: runtimeLoading, error: runtimeError, refreshRuntime } = useOrgRuntime();
  const org          = useOrganization();
  const subscription = useSubscription();
  const limits       = useLimits();
  const usage        = useUsage();
  const features     = useOrgFeatures();

  const [tab, setTab]               = useState('overview');
  const [licenses, setLicenses]     = useState([]);
  const [licLoading, setLicLoading] = useState(true);
  const [licError, setLicError]     = useState(null);
  const [history, setHistory]       = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  const loadLicenses = useCallback(async () => {
    setLicLoading(true);
    setLicError(null);
    try { setLicenses((await licenseApi.listByOrg(orgId)) ?? []); }
    catch (err) { setLicError(err?.message ?? 'Failed to load licenses'); }
    finally { setLicLoading(false); }
  }, [orgId]);

  const loadHistory = useCallback(async () => {
    setHistLoading(true);
    try { setHistory((await licenseApi.getHistory(orgId)) ?? []); }
    catch { setHistory([]); }
    finally { setHistLoading(false); }
  }, [orgId]);

  useEffect(() => { loadLicenses(); }, [loadLicenses]);
  useEffect(() => { if (tab === 'history') loadHistory(); }, [tab, loadHistory]);

  if (runtimeLoading) return (
    <div className="nx-cc-page">
      <PageHead title="Licenses" sub="Loading…" />
      <LoadingSkeleton rows={5} />
    </div>
  );

  if (runtimeError) return (
    <div className="nx-cc-page">
      <PageHead title="Licenses" sub="Error" />
      <div className="nx-empty" style={{ flexDirection: 'column', gap: 8 }}>
        <NexusIcon name="AlertCircle" size={28} style={{ color: 'var(--nx-danger)' }} />
        <span style={{ fontWeight: 600 }}>Failed to load organization data</span>
        <span className="nx-muted nx-mini">{runtimeError}</span>
        <Btn variant="ghost" size="sm" onClick={refreshRuntime}>Retry</Btn>
      </div>
    </div>
  );

  if (!perm.has(ORG.LICENSES_VIEW) && !perm.isSA) return (
    <div className="nx-cc-page">
      <DeniedState reason="insufficient_permission" message="Licenses requires the Licenses View capability. Contact your org admin." />
    </div>
  );

  const orgName = org?.name ?? 'this organization';
  return (
    <div className="nx-cc-page">
      <PageHead
        title="Licenses"
        sub={`License allocation and entitlements for ${orgName}`}
        back={returnTo ? { label: `Back to ${returnLabel}`, onClick: () => navigate(returnTo) } : undefined}
        actions={<Btn variant="ghost" size="sm" icon="RefreshCw" onClick={refreshRuntime}>Refresh</Btn>}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)', marginBottom: 16, fontSize: 13, color: 'var(--nx-text-muted)' }}>
        <NexusIcon name="Info" size={14} style={{ flexShrink: 0, color: 'var(--nx-accent)' }} />
        <span>Licenses are managed by your platform administrator. Contact them to upgrade your plan or request additional allocations.</span>
      </div>

      <TabBar tabs={TABS} active={tab} onChange={setTab} />
      <div className="nx-cc-tab-body">
        {tab === 'overview'    && <OverviewTab org={org} subscription={subscription} limits={limits} usage={usage} licenses={licenses} />}
        {tab === 'allocations' && <AllocationTab licenses={licenses} loading={licLoading} error={licError} />}
        {tab === 'features'    && <FeaturesTab features={features} />}
        {tab === 'insights'    && <InsightsTab licenses={licenses} limits={limits} usage={usage} />}
        {tab === 'history'     && <HistoryTab org={org} subscription={subscription} history={history} historyLoading={histLoading} />}
      </div>
    </div>
  );
}
